import { CheckOutlined, CloseOutlined, ReloadOutlined } from "@ant-design/icons";
import type { InputRef } from "antd";
import { Button, Col, DatePicker, Form, Input, message, Row, Statistic, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import React, { useContext, useEffect, useRef, useState } from "react";
import { ContentBox } from "../../../components/layout/content";
import { LoginedLayout } from "../../../components/layout/loginLayout";
import { HeaderTitle } from "../../../components/styled-components";
import { UserContext } from "../../../context/userContext";
import { GET, POST } from "../../../services";
import { CONFIRM_SHIPPING, GET_LIST_CHECKOUT, GET_UNCHECKOUT_ORDER } from "../../../services/path";
import { PlaySound } from "../../../functions/function";

interface DataType {
  logistic: string;
  trackingNo: string;
  orderNo: string;
  soNo: string;
  checkoutDate: string;
  checkoutStatus: number;
  isDuplicate: number;
}

interface UncheckoutData {
  order_no: string;
  logistic: string;
  shipping_type: string;
  pack_date: string;
  create_date: string;
  opt_status_id: number;
  category: string;
}

interface CheckoutPageProps {
  pageType: string;
}

enum ShippingType {
  normal = "LOGISTIC",
  express = "EXPRESS",
}

const CheckoutPage: React.FC<CheckoutPageProps> = ({ pageType }) => {
  const inputRef = useRef<InputRef>(null);
  const [data, setData] = useState<DataType[]>([]);
  const [dataerror, setDataerror] = useState<DataType[]>([]);
  const [dataDuplicate, setDataDuplicate] = useState<DataType[]>([]);
  const [trackingNo, setTrackingNo] = useState<string>("");
  const [expressOrder, setExpressOrder] = useState<UncheckoutData[]>([]);
  const [normalOrder, setNormalOrder] = useState<UncheckoutData[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const { user } = useContext(UserContext);

  const tableStyle: React.CSSProperties = {
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)", // Soft shadow
    borderRadius: "8px", // Optional: Smooth corners
    overflow: "hidden", // Prevents shadow cutoff
  };

  // Handle updating checkoutDate trackingNo is found
  const handleCheckout = async () => {
    if (trackingNo !== "") {
      const datacheckout = {
        trackingNo: trackingNo,
        warehouse: pageType,
        checkoutDate: dayjs().format("YYYY-MM-DD"),
        userCreate: user.user_id,
      };

      try {
        const resultCheckoutorder = await POST(CONFIRM_SHIPPING, datacheckout);
        if (resultCheckoutorder.result.checkoutStatus !== 1) {
          PlaySound("success");
          setData((prevData) => [resultCheckoutorder.result, ...prevData]);
          fetchUnCheckOutData();
        } else {
          if (resultCheckoutorder.result.isDuplicate === 1) {
            PlaySound("duplicate");
            setDataDuplicate((prevDataDuplicate) => [
              resultCheckoutorder.result,
              ...prevDataDuplicate,
            ]);
          } else {
            PlaySound("error");
            setDataerror((prevDataError) => [resultCheckoutorder.result, ...prevDataError]);
          }
        }
      } catch (error) {
        PlaySound("error");
        message.error(`พบข้อผิดพลาด`);
      } finally {
        setTrackingNo(""); // Reset input after checkout
      }
    }
  };

  useEffect(() => {
    inputRef.current?.focus();
    window.scrollTo(0, 0);
  }, []);

  const disableOtherDates = (current: Dayjs) => {
    const today = dayjs();
    const yesterday = today.subtract(1, "day");
    return !current.isSame(today, "day") && !current.isSame(yesterday, "day");
  };

  const handleDateChange = (date: Dayjs | null) => {
    setSelectedDate(date ? date.format("YYYY-MM-DD") : ""); // Set to empty string when cleared
  };

  useEffect(() => {
    if (selectedDate) {
      fetchDataTable();
      fetchUnCheckOutData();
    }
  }, [selectedDate]);

  const fetchDataTable = async () => {
    try {
      const result = await GET(GET_LIST_CHECKOUT(selectedDate, "shipping", pageType));
      const list: DataType[] = result.result ?? [];

      if (list.length === 0) {
        setData([]);
        setDataDuplicate([]);
        setDataerror([]);
        return;
      }

      message.success("ดึงข้อมูลสำเร็จ");

      const pending: DataType[] = [];
      const duplicate: DataType[] = [];
      const error: DataType[] = [];

      for (const item of list) {
        if (item.checkoutStatus === 0) pending.push(item);
        else if (item.isDuplicate === 1) duplicate.push(item);
        else error.push(item);
      }

      setData(pending);
      setDataDuplicate(duplicate);
      setDataerror(error);
    } catch {
      message.error("พบข้อผิดพลาด");
    }
  };

  const columns: ColumnsType<DataType> = [
    { title: "Logistic", dataIndex: "logistic", key: "logistic" },
    { title: "Tracking No", dataIndex: "trackingNo", key: "trackingNo" },
    { title: "Order Number", dataIndex: "orderNo", key: "orderNumber" },
    {
      title: "Checked Out",
      key: "checkedOut",
      render: (_: any, record: DataType) =>
        record.checkoutStatus === 0 ? (
          <CheckOutlined style={{ color: "green" }} />
        ) : (
          <CloseOutlined style={{ color: "red" }} />
        ),
    },
    { title: "SO", dataIndex: "soNo", key: "soNo" },
    { title: "Inv No", dataIndex: "invNo", key: "invNo" },
    {
      title: "Checkout Date",
      dataIndex: "checkoutDate",
      key: "checkoutDate",
      sorter: (a: DataType, b: DataType) =>
        new Date(a.checkoutDate).getTime() - new Date(b.checkoutDate).getTime(),
      defaultSortOrder: "descend",
    },
  ];

  const fetchUnCheckOutData = async () => {
    const isMatchPageCategory = (item: UncheckoutData): boolean =>
      (pageType === "MMT" && item.category === "checker") ||
      (pageType === "RBN" && item.category === "bulky");

    const isToday = (date: string): boolean =>
      dayjs(date).format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD");

    try {
      const data: UncheckoutData[] = (await GET(GET_UNCHECKOUT_ORDER))?.result ?? [];

      if (data.length === 0) {
        setExpressOrder([]);
        setNormalOrder([]);
        return;
      }

      setExpressOrder(
        data.filter(
          (item) =>
            item.shipping_type === ShippingType.express &&
            isToday(item.pack_date) &&
            isMatchPageCategory(item),
        ),
      );

      setNormalOrder(
        data.filter(
          (item) =>
            item.shipping_type === ShippingType.normal &&
            isToday(item.pack_date) &&
            isMatchPageCategory(item),
        ),
      );
    } catch {
      message.error("พบข้อผิดพลาด");
    }
  };

  return (
    <LoginedLayout>
      <ContentBox style={{ backgroundColor: "#b6eefc" }}>
        <HeaderTitle>
          <h2>Check-Out Order {pageType}</h2>
        </HeaderTitle>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={16}>
            <Form layout="inline">
              <Form.Item label="Date">
                <DatePicker
                  value={selectedDate ? dayjs(selectedDate, "YYYY-MM-DD") : undefined} // Show undefined when cleared
                  onChange={handleDateChange}
                  format="DD-MM-YYYY"
                  disabledDate={disableOtherDates}
                  style={{ width: 150, marginBottom: 20 }}
                  allowClear
                />
              </Form.Item>
              <Form.Item label="Reload">
                <Button
                  onClick={() => {
                    fetchDataTable();
                    fetchUnCheckOutData();
                  }}
                >
                  <ReloadOutlined />
                </Button>
              </Form.Item>
            </Form>
          </Col>
          <Col xs={24} md={8}>
            <h2 style={{ textAlign: "center", marginBottom: "10px", marginTop: "0" }}>
              Pending Check-Out
            </h2>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "10px",
              }}
            >
              <Statistic
                title={<div style={{ textAlign: "center", fontSize: 13 }}>Express Order</div>}
                value={expressOrder.length}
                valueStyle={{
                  fontWeight: 700,
                  fontSize: 18,
                  textAlign: "center",
                  lineHeight: "1.2",
                  color: "#f26c03",
                }}
                style={{
                  minWidth: 200,
                  padding: "8px",
                  border: `1.5px solid ${"#f26c03"}`,
                  borderRadius: 8,
                  boxShadow: `0px 4px 10px ${"#f26c03"}`,
                  backgroundColor: "#ffffff",
                }}
              />
              <Statistic
                title={<div style={{ textAlign: "center", fontSize: 13 }}>Normal Order</div>}
                value={normalOrder.length}
                valueStyle={{
                  fontWeight: 700,
                  fontSize: 18,
                  textAlign: "center",
                  lineHeight: "1.2",
                  color: "#00d9ff",
                }}
                style={{
                  minWidth: 200,
                  padding: "8px",
                  border: `1.5px solid ${"#00d9ff"}`,
                  borderRadius: 8,
                  boxShadow: `0px 4px 10px ${"#00d9ff"}`,
                  backgroundColor: "#ffffff",
                }}
              />
            </div>
          </Col>
        </Row>
        <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <Input
            ref={inputRef}
            placeholder="Enter tracking number"
            value={trackingNo}
            onChange={(e) => setTrackingNo(e.target.value)}
            onPressEnter={handleCheckout}
            style={{ width: 400, marginBottom: 10 }}
          />
        </div>
        <div style={{ padding: "20px", gap: "20px" }}>
          <Row gutter={[16, 16]} justify="start">
            {/* Left Side: Platform Cancel */}
            <Col xs={24} md={12}>
              <h2>Platform Cancel</h2>
              <Table
                dataSource={dataerror}
                columns={columns}
                rowKey="orderNumber"
                style={{ ...tableStyle, marginTop: 10 }}
                scroll={{ x: "max-content" }}
                onRow={(record) => ({
                  style: record.isDuplicate === 1 ? { backgroundColor: "yellow" } : {},
                })}
              />
            </Col>
            {/* Right Side: Complete */}
            <Col xs={24} md={12}>
              <h2>Complete</h2>
              <Table
                dataSource={data}
                columns={columns}
                rowKey="orderNumber"
                style={{ ...tableStyle, marginTop: 10 }}
                scroll={{ x: "max-content" }}
              />
            </Col>
            {/* Left Side: duplicate */}
            <Col xs={24} md={12}>
              <h2>Duplicate</h2>
              <Table
                dataSource={dataDuplicate}
                columns={columns}
                rowKey="orderNumber"
                style={{ ...tableStyle, marginTop: 10 }}
                scroll={{ x: "max-content" }}
              />
            </Col>
          </Row>
        </div>
      </ContentBox>
    </LoginedLayout>
  );
};

export default CheckoutPage;
